import cookies from 'js-cookie';
import { trackGtmEvent } from '@motionelements/core/src/helpers/gtm.js';

const QUERY_KEY_EXPERIMENT_VARIATION = 'x';
/** Short-lived cookie to prevent reload loops (auto-expires; re-entry after TTL can retry). */
const EXPERIMENT_VARIATION_RELOAD_COOKIE_PREFIX = 'ME_exp_reload_';
const EXPERIMENT_VARIATION_RELOAD_TTL_SECONDS = 15;

function experimentVariationReloadCookieName(experimentId) {
    return `${EXPERIMENT_VARIATION_RELOAD_COOKIE_PREFIX}${experimentId}`;
}

function hasExperimentVariationReloadCookie(experimentId) {
    const value = cookies.get(experimentVariationReloadCookieName(experimentId));
    return value != null && value !== '';
}

function setExperimentVariationReloadCookie(experimentId) {
    cookies.set(experimentVariationReloadCookieName(experimentId), '1', {
        expires: new Date(Date.now() + (EXPERIMENT_VARIATION_RELOAD_TTL_SECONDS * 1000)),
        path: '/',
        sameSite: 'Lax',
    });
}

function clearExperimentVariationReloadCookie(experimentId) {
    cookies.remove(experimentVariationReloadCookieName(experimentId), { path: '/' });
}

/** Flagsmith slug for the default / 2026 subscribe experience (matches edge worker). */
export const EXPERIMENT_CONTROL_VARIATION_CODE = 'control';

/** GA4 / GTM / cookie experiment id for subscribe page A/B (matches worker). */
export const SUBSCRIBE_PAGE_EXPERIMENT_ID = 'subscribe_page_plus_ai';

function experimentCookieName(experimentId) {
    return `ME_exp_${experimentId}`;
}

export function isExperimentControlVariation(variationCode) {
    return variationCode == null || variationCode === '' || variationCode === EXPERIMENT_CONTROL_VARIATION_CODE;
}

/** ?x= → Flagsmith-style slug for GTM (per experiment). */
const EXPERIMENT_X_TO_VARIATION_CODE = {
    [SUBSCRIBE_PAGE_EXPERIMENT_ID]: {
        '0': EXPERIMENT_CONTROL_VARIATION_CODE, // 'control'
        '1': 'no_ai',
    },
};

export default {
    computed: {
        /**
         * Edge / AB query ?x= as a string (e.g. '0', '1') — UI bucket only.
         * null when missing. Handles duplicate keys (?x=0&x=1 → first value).
         */
        experimentVariation() {
            if (!this.$route) {
                return null;
            }
            const raw = this.$route.query[QUERY_KEY_EXPERIMENT_VARIATION];
            const value = Array.isArray(raw) ? raw[0] : raw;
            if (value == null || value === '') {
                return null;
            }
            return String(value);
        },
        hasExperimentVariation() {
            // != null: check for null or undefined
            return this.experimentVariation != null;
        },
    },
    methods: {
        /**
         * Flagsmith variation slug from ME_exp_{experimentId} (set at edge on /subscribe).
         */
        getExperimentVariationCode(experimentId) {
            if (typeof process !== 'undefined' && process.server) {
                return null;
            }
            if (typeof window === 'undefined') {
                return null;
            }
            const value = cookies.get(experimentCookieName(experimentId));
            if (value == null || value === '') {
                return null;
            }
            return String(value);
        },
        /**
         * Cookie slug and/or ?x= present — safe to render (not waiting on edge assignment).
         */
        hasExperimentAssignment(experimentId) {
            if (this.hasExperimentVariation) {
                return true;
            }
            return this.getExperimentVariationCode(experimentId) != null;
        },
        /**
         * SPA nav skips the edge worker AB redirect — hard-nav so worker can set ?x=.
         * @param {string} experimentId
         * @returns {boolean} true if a reload/navigation was scheduled (caller should abort further work)
         */
        ensureExperimentVariation(experimentId) {
            if (typeof process !== 'undefined' && process.server) {
                return false;
            }
            if (typeof window === 'undefined') {
                return false;
            }
            if (this.hasExperimentVariation) {
                clearExperimentVariationReloadCookie(experimentId);
                return false;
            }
            // Already tried reload within TTL and still no ?x= — soft-fill control so UI can render.
            // Do not track experiment_impression — assignment did not come from the edge.
            if (hasExperimentVariationReloadCookie(experimentId)) {
                clearExperimentVariationReloadCookie(experimentId);
                if (this.$rollbar) {
                    this.$rollbar.warning('experiment variation: infinite reload', {
                        experimentId,
                        route: this.$route && this.$route.fullPath,
                        query: this.$route && this.$route.query,
                    });
                }
                if (this.$router && this.$route) {
                    this.$router.replace({
                        query: {
                            ...this.$route.query,
                            [QUERY_KEY_EXPERIMENT_VARIATION]: '0',
                        },
                    }).catch(() => {});
                }
                return true;
            }
            setExperimentVariationReloadCookie(experimentId);
            // Defer past Vue Router SPA transition — reload() in created() is often cancelled.
            // location.replace(same href) is a no-op; reload hits the edge worker AB 302.
            window.setTimeout(() => {
                window.location.reload();
            }, 0);
            return true;
        },
        /**
         * Edge assignment + GTM impression (use from activated() on keep-alive pages).
         */
        initExperiment(experimentId) {
            if (this.ensureExperimentVariation(experimentId)) {
                return;
            }
            this.trackExperimentImpression(experimentId);
        },
        /**
         * Variation slug for GTM — subscribe uses ?x= exposure map; others prefer cookie.
         */
        resolveExperimentVariationCode(experimentId) {
            const x = this.experimentVariation;
            if (x == null) {
                return null;
            }
            const map = EXPERIMENT_X_TO_VARIATION_CODE[experimentId];
            if (map) {
                return map[x] ?? null;
            }
            return x; // default: raw ?x=
        },
        /**
         * Push experiment assignment + fire experiment_impression for GTM.
         * @param {string} experimentId
         * @param {string} [variation] optional override for analytics slug
         */
        trackExperimentImpression(experimentId, variation) {
            if (typeof process !== 'undefined' && process.server) {
                return;
            }
            if (typeof window === 'undefined') {
                return;
            }

            const resolvedVariation = variation != null && variation !== ''
                ? String(variation)
                : this.resolveExperimentVariationCode(experimentId);

            if (resolvedVariation == null || resolvedVariation === '') {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                experiment_id: experimentId,
                variation_id: `${experimentId}.${resolvedVariation}`,
            });

            if (this.$gtm) {
                trackGtmEvent(this, {
                    event: 'experiment_impression',
                });
            } else {
                window.dataLayer.push({
                    event: 'experiment_impression',
                });
            }
        },
    },
};
